import React, { useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { useModerator } from '../../hooks/useModerator';
import { Button } from '../common/Button';
import { CommentModerationActions } from '../moderation/CommentModerationActions';
import { FlagCommentButton } from './FlagCommentButton';
import type { Comment } from '../../types/comment';
import { formatRelativeTime } from '../../utils/dateUtils';
import { UserAvatar } from '../common/UserAvatar';
import { ProfilePictureModal } from '../common/ProfilePictureModal';
import { useLanguage } from '../../i18n/useLanguage';

interface CommentCardProps {
  comment: Comment;
  onEdit?: (comment: Comment) => void;
  onDelete?: (commentId: string) => void;
  onRefresh?: () => void;
  onFlagged?: () => void;
  postAuthorId?: string;
  onMessageAuthor?: (comment: Comment) => void;
  /** Renders inside the card after meta/actions (e.g. profile activity row). */
  bottomBar?: React.ReactNode;
}

export const CommentCard: React.FC<CommentCardProps> = ({ 
  comment, 
  onEdit, 
  onDelete, 
  onRefresh,
  onFlagged,
  postAuthorId,
  onMessageAuthor,
  bottomBar,
}) => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const isModerator = useModerator();
  const isAuthor = user?.sub === comment.authorId;
  const isPostAuthor = user?.sub === postAuthorId;
  const canMessageAuthor = isPostAuthor && !comment.deleted && comment.authorId && comment.authorId !== postAuthorId;
  const [showProfileModal, setShowProfileModal] = useState(false);

  return (
    <div className={`comment-card ${comment.deleted ? 'comment-card--deleted' : ''}`}>
      {comment.deleted && (
        <div className="comment-deleted-badge">
          <span>{t('post.deleted')}</span>
        </div>
      )}
      <div className="comment-content">
        <p className={comment.deleted ? 'comment-content--deleted' : ''}>{comment.content}</p>
      </div>
      <div className="comment-meta">
        <span className="comment-time">{formatRelativeTime(comment.createdAt, language)}</span>
        {comment.authorUsername && comment.authorUsername.trim() && (
          <>
            <span className="comment-by">{t('post.by')}</span>
            <span className="comment-author">{comment.authorUsername}</span>
            {comment.authorId && (
              <UserAvatar 
                userId={comment.authorId} 
                username={comment.authorUsername} 
                size={24}
                onClick={() => setShowProfileModal(true)}
              />
            )}
          </>
        )}
        {comment.deleted && (
          <span className="comment-deleted">({t('post.deleted').toLowerCase()})</span>
        )}
      </div>

      {!comment.deleted && (
        <div className="comment-actions">
          {/* Show Edit and Delete buttons only for author (not moderators) */}
          {isAuthor && !isModerator && (onEdit || onDelete) && (
            <>
              {onEdit && (
                <Button variant="outline" size="small" onClick={() => onEdit(comment)}>
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button variant="danger" size="small" onClick={() => onDelete(comment.id)}>
                  Delete
                </Button>
              )}
            </>
          )}
          {/* Show Edit and Soft Delete buttons for moderators */}
          {isModerator && (
            <>
              {onEdit && (
                <Button variant="outline" size="small" onClick={() => onEdit(comment)}>
                  Edit
                </Button>
              )}
              <CommentModerationActions
                commentId={comment.id}
                isDeleted={comment.deleted}
                onDeleted={onRefresh}
                onRestored={onRefresh}
              />
            </>
          )}
          {/* Show Report (flag) for non-author when comment is not already pending flag */}
          {!comment.deleted && comment.flagStatus !== 'PENDING' && comment.authorId && !isAuthor && (
            <FlagCommentButton
              commentId={comment.id}
              authorId={comment.authorId}
              onFlagged={onFlagged ?? onRefresh}
              variant="text"
            />
          )}
          {/* Show Message Comment Author button for post author */}
          {canMessageAuthor && onMessageAuthor && (
            <Button 
              variant="outline" 
              size="small" 
              onClick={() => onMessageAuthor(comment)}
            >
              Message Author
            </Button>
          )}
        </div>
      )}

      {bottomBar && !comment.deleted && (
        <div className="comment-card-bottom-bar">{bottomBar}</div>
      )}
      
      {comment.authorId && (
        <ProfilePictureModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userId={comment.authorId}
          username={comment.authorUsername}
        />
      )}
    </div>
  );
};

